import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  IconLoader,
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../hoc/new-application/types';
import { GroupOfCompaniesTypes } from './types';
import StepPartBlock from '../../common/step-part-block';
import InfoRow from '../../common/info-row';

import commonStyles from '../../common/styles.module.css';

const GroupOfCompanies = ({
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  status,
  isEditable,
  countries,
  sourceOfWealth,
  baseCurrency,
  questionnarieSetValue,
  questionnarieFields,
  questionnarieIsLoading,
}: GroupOfCompaniesTypes) => (
  <StepPartBlock title="Group of companies" zIndex={15}>
    <>
      {questionnarieIsLoading && (
        <div className={commonStyles.dictionaryLoader}>
          <IconLoader />
        </div>
      )}
      {!questionnarieIsLoading && (
        <>
          <InfoRow
            title="Does the Customer belong to any group of companies?"
            id="belongsToGroup"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="belongsToGroup"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['belongsToGroup']);

                    if (e.target.value === 'no') {
                      questionnarieClearErrors([
                        'parentCompanyName',
                        'parentCompanyCountryId',
                        'subsidiariesInfo',
                        'mainActivities',
                        'groupWealthSourceIds',
                        'estimatedTotalWealthEuro',
                      ]);

                      questionnarieSetValue('parentCompanyName', '');
                      questionnarieSetValue('parentCompanyCountryId', '');
                      questionnarieSetValue('subsidiariesInfo', '');
                      questionnarieSetValue('mainActivities', '');
                      questionnarieSetValue('groupWealthSourceIds', []);
                      questionnarieSetValue('estimatedTotalWealthEuro', '');
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.belongsToGroup?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.belongsToGroup === 'yes' && (
            <>
              <InfoRow
                title="Provide the name of the parent or managing company"
                id="parentCompanyName"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="parentCompanyName"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      placeholder="Enter name..."
                      value={value}
                      size={InputSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['parentCompanyName']);
                      }}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={questionnarieErrors?.parentCompanyName?.message}
                    />
                  )}
                />
              </InfoRow>

              <InfoRow
                title="Country of incorporation of the parent or managing company"
                id="parentCompanyCountryId"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="parentCompanyCountryId"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Autocomplete
                      name={name}
                      placeholder="Select country..."
                      list={countries}
                      value={value}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['parentCompanyCountryId']);
                      }}
                      selectWrapperClassName={
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? commonStyles.selectDisabled
                          : ''
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={
                        questionnarieErrors?.parentCompanyCountryId?.message
                      }
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Subsidiary companies and associate companies of the group and countries of their incorporation"
                id="subsidiariesInfo"
                className={commonStyles.noFlex}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="subsidiariesInfo"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Textarea
                      name={name}
                      title=""
                      placeholder="Enter description..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['subsidiariesInfo']);
                      }}
                      className={`borderless ${commonStyles.stepOneTextarea} ${commonStyles.pepTextarea}`}
                      error={questionnarieErrors?.subsidiariesInfo?.message}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Group’s main business activities"
                id="mainActivities"
                className={commonStyles.noFlex}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="mainActivities"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Textarea
                      name={name}
                      title=""
                      placeholder="Enter description..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['mainActivities']);
                      }}
                      className={`borderless ${commonStyles.stepOneTextarea} ${commonStyles.pepTextarea}`}
                      error={questionnarieErrors?.mainActivities?.message}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Source of wealth of the group (origin of total assets)"
                id="groupWealthSourceIds"
                style={{
                  position: 'relative',
                  zIndex: 15,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="groupWealthSourceIds"
                  defaultValue={[]}
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select an options..."
                      list={
                        sourceOfWealth?.map((item) => ({
                          name: item.value,
                          value: item.id,
                        })) || []
                      }
                      value={value}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['groupWealthSourceIds']);

                        if (
                          !(e.target.value as string[])?.includes('Other') &&
                          questionnarieFields?.groupWealthSourceIds
                        ) {
                          questionnarieClearErrors(['groupWealthSourceOther']);
                          questionnarieSetValue('groupWealthSourceOther', '');
                        }
                      }}
                      selectWrapperClassName={
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? commonStyles.selectDisabled
                          : ''
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={questionnarieErrors?.groupWealthSourceIds?.message}
                      mode={SelectType.MULTIPLE}
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              {questionnarieFields?.groupWealthSourceIds?.find((item) =>
                sourceOfWealth
                  ?.find((client) => item === client.id)
                  ?.value?.includes('Other'),
              ) && (
                <InfoRow
                  title="Provide details"
                  id="groupWealthSourceOther"
                  className={commonStyles.noFlex}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="groupWealthSourceOther"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Textarea
                        name={name}
                        title=""
                        placeholder="Enter details..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['groupWealthSourceOther']);
                        }}
                        className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                        error={
                          questionnarieErrors?.groupWealthSourceOther?.message
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              <InfoRow
                title={`Estimated total wealth of the group (in ${baseCurrency?.name})`}
                id="estimatedTotalWealthEuro"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="estimatedTotalWealthEuro"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      placeholder="Enter amount..."
                      value={value}
                      size={InputSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['estimatedTotalWealthEuro']);
                      }}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={
                        questionnarieErrors?.estimatedTotalWealthEuro?.message
                      }
                      currency={baseCurrency}
                      amountInput
                    />
                  )}
                />
              </InfoRow>
            </>
          )}
        </>
      )}
    </>
  </StepPartBlock>
);

export default GroupOfCompanies;
